import { ChangeDetectorRef, Component, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { EmployeesService, RegisterEmployeeBody } from '../../services/employee.service';
import {
  Company,
  CountryPhoneCode,
  Department,
  Employee,
  EmployeeListFilters,
  EmploymentStatus,
  PayGroup,
  Person,
  Shift,
  WorkOrder,
  WorkerType,
} from '../../models/employee.model';
import {
  SearchSelectComponent,
  SearchSelectOption,
} from '../search-select/search-select.component';
import { FilterService } from '../../../services/filter.service';

interface EmployeeListFiltersVm {
  companyId: number | null;
  departmentId: number | null;
  employmentStatus: EmploymentStatus | null;
  searchTerm: string;
}

type ToastKind = 'success' | 'error' | 'info';

interface CreateEmployeeForm {
  employeeNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  hireDate: string;
  phoneCountryIso2: string;
  phoneLocalNumber: string;
  email: string;
  rfid: string;
  departmentId: number | null;
  defaultWorkOrderNumberId: number | null;
  companyId: number | null;
  payGroupId: number | null;
  shiftId: number | null;
  workerTypeId: number | null;
  supervisorUserId: number | null;
}

type RequiredCreateField =
  | 'employeeNumber'
  | 'firstName'
  | 'lastName'
  | 'hireDate'
  | 'phoneLocalNumber'
  | 'email'
  | 'departmentId'
  | 'companyId'
  | 'payGroupId'
  | 'shiftId'
  | 'workerTypeId';

@Component({
  selector: 'app-employee-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SearchSelectComponent],
  templateUrl: './employee-list.component.html',
  styleUrls: ['./employee-list.component.scss'],
})
export class EmployeeListComponent implements OnInit, OnDestroy {
  employees: Employee[] = [];
  isLoading = false;
  isExportingEmployees = false;
  errorMessage: string | null = null;

  filterService = inject(FilterService);

  currentPage = 1;
  pageSize = 50;
  totalPages = 1;
  totalCount = 0;
  pageSizeOptions = [50, 100, 200, 500];

  totalEmployeeCount = 0;
  missingEmployeeCount = 0;

  readonly employmentStatusOptions: EmploymentStatus[] = ['Active', 'Inactive', 'Terminated'];

  filters: EmployeeListFiltersVm = {
    companyId: null,
    departmentId: null,
    employmentStatus: null,
    searchTerm: '',
  };

  toastMessage: string | null = null;
  toastKind: ToastKind = 'info';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  isCreateModalOpen = false;
  isCreateModalLoading = false;
  hasLoadedCreateModalData = false;
  createModalError: string | null = null;
  createSubmitError: string | null = null;
  isCreatingEmployee = false;

  createForm: CreateEmployeeForm = this.getDefaultCreateForm();

  createCompanies: Company[] = [];
  createDepartments: Department[] = [];
  createPayGroups: PayGroup[] = [];
  createWorkerTypes: WorkerType[] = [];
  createShifts: Shift[] = [];
  createOverheadWorkOrders: WorkOrder[] = [];
  createSupervisors: Person[] = [];
  countryPhoneCodes: CountryPhoneCode[] = [];

  private readonly searchInput$ = new Subject<string>();

  constructor(
    private readonly employeesService: EmployeesService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
  ) { }

  ngOnInit(): void {
    this.countryPhoneCodes = this.employeesService.getCountryPhoneCodes();
    this.bindSearch();
    void this.loadEmployees();
    this.loadMissingEmployeeCount();
  }

  get hasActiveFilters(): boolean {
    return (
      this.filters.companyId !== null ||
      this.filters.departmentId !== null ||
      this.filters.employmentStatus !== null ||
      !!this.filters.searchTerm.trim()
    );
  }

  get countryPhoneCodeOptions(): SearchSelectOption<string>[] {
    return this.countryPhoneCodes.map((country) => ({
      id: country.iso2,
      label: `${country.name} (+${country.dialCode})`,
      subLabel: country.iso2,
    }));
  }

  get createDepartmentOptions(): SearchSelectOption<number>[] {
    return this.createDepartments
      .slice()
      .sort((a, b) => Number(a.code) - Number(b.code))
      .map((department) => ({
        id: department.id,
        label: `${department.code} — ${department.name}`,
      }));
  }

  get createCompanyOptions(): SearchSelectOption<number>[] {
    return this.createCompanies
      .slice()
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map((company) => ({
        id: company.id,
        label: company.name,
        subLabel: company.description ?? '',
      }));
  }

  get createPayGroupOptions(): SearchSelectOption<number>[] {
    return this.createPayGroups
      .slice()
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map((payGroup) => ({
        id: payGroup.id,
        label: payGroup.name,
      }));
  }

  get createWorkerTypeOptions(): SearchSelectOption<number>[] {
    return this.createWorkerTypes
      .slice()
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map((workerType) => ({
        id: workerType.id,
        label: workerType.name,
      }));
  }

  get createShiftOptions(): SearchSelectOption<number>[] {
    return this.createShifts
      .slice()
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map((shift) => ({
        id: shift.id,
        label: shift.name,
        subLabel: shift.payCodes?.length
          ? shift.payCodes.map((payCode) => payCode.name).join(', ')
          : '',
      }));
  }

  get createOverheadWonOptions(): SearchSelectOption<number>[] {
    return this.createOverheadWorkOrders
      .slice()
      .sort((a, b) => (a.won ?? '').localeCompare(b.won ?? ''))
      .map((workOrder) => ({
        id: workOrder.id,
        label: workOrder.won,
        subLabel: workOrder.description ?? '',
      }));
  }

  get createSupervisorOptions(): SearchSelectOption<number>[] {
    return this.createSupervisors
      .slice()
      .sort((a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? ''))
      .map((employee) => ({
        id: employee.id,
        label: `${employee.lastName}, ${employee.firstName}`,
        subLabel: employee.employeeNumber
          ? `Employee # ${employee.employeeNumber}`
          : (employee.email ?? ''),
      }));
  }

  get selectedCreatePhoneCountry(): CountryPhoneCode | null {
    const iso2 = String(this.createForm.phoneCountryIso2 ?? '')
      .trim()
      .toUpperCase();

    if (!iso2) return null;

    return this.countryPhoneCodes.find((country) => country.iso2 === iso2) ?? null;
  }

  get createPhonePreview(): string {
    const fullPhone = this.employeesService.buildPhoneNumberForApi(
      this.selectedCreatePhoneCountry?.dialCode ?? '1',
      this.createForm.phoneLocalNumber,
    );

    return fullPhone ? `+${fullPhone}` : '';
  }

  get createPhoneLocalInputValue(): string {
    return this.formatPhoneLocalForInput(
      this.createForm.phoneLocalNumber,
      this.createForm.phoneCountryIso2,
    );
  }

  get createFormHelpText(): string {
    return 'If the company, department, pay group, shift, worker type, supervisor, or overhead WON you need is not listed, create it first before creating the employee.';
  }

  private bindSearch(): void {
    this.searchInput$.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((value) => {
      this.filters.searchTerm = value;
      void this.loadEmployees(1);
    });
  }

  private buildFilters(): EmployeeListFilters {
    return {
      companyId: this.filters.companyId,
      departmentId: this.filters.departmentId,
      employmentStatus: this.filters.employmentStatus,
      searchTerm: this.filters.searchTerm.trim() || null,
    };
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private getDefaultCreateForm(): CreateEmployeeForm {
    return {
      employeeNumber: '',
      firstName: '',
      middleName: '',
      lastName: '',
      hireDate: '',
      phoneCountryIso2: 'US',
      phoneLocalNumber: '',
      email: '',
      rfid: '',
      departmentId: null,
      defaultWorkOrderNumberId: null,
      companyId: null,
      payGroupId: null,
      shiftId: null,
      workerTypeId: null,
      supervisorUserId: null,
    };
  }

  private showToast(kind: ToastKind, message: string, ms = 2500): void {
    this.toastKind = kind;
    this.toastMessage = message;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = setTimeout(() => {
      this.toastMessage = null;
      this.toastTimer = null;
      this.cdr.detectChanges();
    }, ms);

    this.cdr.detectChanges();
  }

  clearToast(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    this.toastMessage = null;
    this.cdr.detectChanges();
  }

  async loadEmployees(page = 1): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;
    this.errorMessage = null;
    this.cdr.detectChanges();

    try {
      const response = await firstValueFrom(
        this.employeesService.getEmployees(page, this.pageSize, this.buildFilters()),
      );

      this.employees = response.data ?? [];
      this.totalPages = response.totalPages ?? 1;
      this.totalCount = response.totalCount ?? 0;
      this.totalEmployeeCount = this.totalCount;
      this.currentPage = response.currentPage ?? 1;
    } catch {
      this.errorMessage = 'Failed to load employees';
      this.employees = [];
      this.totalPages = 1;
      this.totalCount = 0;
      this.currentPage = 1;
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }
  private loadMissingEmployeeCount(): void {
    this.employeesService.getEmployeesMissingData().subscribe({
      next: (employees) => {
        this.missingEmployeeCount = employees?.length ?? 0;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Failed loading missing employee count:', error);
        this.missingEmployeeCount = 0;
        this.cdr.detectChanges();
      },
    });
  }

  async exportEmployeesToExcel(): Promise<void> {
    if (this.isExportingEmployees) return;

    this.isExportingEmployees = true;
    this.cdr.detectChanges();

    try {
      const exportPageSize = 500;
      let page = 1;
      let totalExportPages = 1;
      const allEmployees: Employee[] = [];

      do {
        const response = await firstValueFrom(
          this.employeesService.getEmployees(page, exportPageSize, this.buildFilters()),
        );

        allEmployees.push(...(response.data ?? []));
        totalExportPages = response.totalPages ?? 1;
        page += 1;
      } while (page <= totalExportPages);

      if (!allEmployees.length) {
        this.showToast('info', 'No employees found to export.');
        return;
      }

      const rows = allEmployees.map((emp) => ({
        'Employee #': emp.employeeNumber ?? '',
        'First Name': emp.firstName ?? '',
        'Last Name': emp.lastName ?? '',
        Email: emp.email ?? '',
        Status: emp.employmentStatus ?? emp.employeeStatus ?? '',
        Company: emp.company?.name ?? '',
        Department: emp.department ? `${emp.department.code} - ${emp.department.name}` : '',
        'Pay Group': emp.payGroup?.name ?? '',
        'Worker Type': emp.workerType?.name ?? '',
        Shift: emp.shift?.name ?? '',
        'Job Title': emp.jobTitle ?? '',
        'Auto Booking': emp.enableAutoBooking ? 'Yes' : 'No',
      }));

      const xlsx = await import('xlsx');
      const worksheet = xlsx.utils.json_to_sheet(rows);
      const workbook = xlsx.utils.book_new();

      xlsx.utils.book_append_sheet(workbook, worksheet, 'Employees');
      xlsx.writeFile(workbook, `employees_${this.getExportDateStamp()}.xlsx`);

      this.showToast('success', 'Employee export completed.');
    } catch {
      this.showToast('error', 'Failed to export employees.');
    } finally {
      this.isExportingEmployees = false;
      this.cdr.detectChanges();
    }
  }

  private getExportDateStamp(): string {
    const now = new Date();

    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}`;
  }

  async openCreateModal(): Promise<void> {
    this.ngZone.run(() => {
      this.isCreateModalOpen = true;
      this.isCreateModalLoading = !this.hasLoadedCreateModalData;
      this.createSubmitError = null;
      this.createModalError = null;
      document.body.style.overflow = 'hidden';
      this.cdr.markForCheck();
    });

    if (this.hasLoadedCreateModalData) {
      return;
    }

    try {
      const [
        companies,
        departments,
        payGroups,
        workerTypes,
        shifts,
        overheadWorkOrders,
        supervisors,
      ] = await Promise.all([
        firstValueFrom(this.employeesService.getAllCompanies(200)),
        firstValueFrom(this.employeesService.getAllDepartments(200)),
        firstValueFrom(this.employeesService.getPayGroups(200)),
        firstValueFrom(this.employeesService.getWorkerTypes(200)),
        firstValueFrom(this.employeesService.getShifts(200)),
        firstValueFrom(this.employeesService.getOverheadWorkOrders(200)),
        firstValueFrom(this.employeesService.getAllEmployeesForSupervisorSelect(200)),
      ]);

      this.ngZone.run(() => {
        this.createCompanies = companies;
        this.createDepartments = departments;
        this.createPayGroups = payGroups;
        this.createWorkerTypes = workerTypes;
        this.createShifts = shifts;
        this.createOverheadWorkOrders = overheadWorkOrders;
        this.createSupervisors = supervisors;
        this.hasLoadedCreateModalData = true;
        this.createModalError = null;
        this.isCreateModalLoading = false;
        this.cdr.markForCheck();
      });
    } catch {
      this.ngZone.run(() => {
        this.createModalError = 'Failed to load create employee form data.';
        this.isCreateModalLoading = false;
        this.cdr.markForCheck();
      });
    }
  }

  closeCreateModal(): void {
    if (this.isCreatingEmployee) return;

    this.ngZone.run(() => {
      this.isCreateModalOpen = false;
      this.isCreateModalLoading = false;
      this.createModalError = null;
      this.createSubmitError = null;
      this.createForm = this.getDefaultCreateForm();
      document.body.style.overflow = '';
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.searchInput$.complete();
    document.body.style.overflow = '';

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    this.cdr.markForCheck();
  }

  onCreatePhoneCountryChange(value: string | number | null): void {
    this.createForm.phoneCountryIso2 = String(value ?? '')
      .trim()
      .toUpperCase();
  }

  onCreatePhoneLocalNumberInput(value: string): void {
    const digitsOnly = String(value ?? '').replace(/\D/g, '');
    this.createForm.phoneLocalNumber = digitsOnly;
  }

  onEmploymentStatusChange(value: EmploymentStatus | string | null): void {
    const normalized = String(value ?? '').trim();

    this.filters.employmentStatus = this.isEmploymentStatus(normalized) ? normalized : null;
    void this.loadEmployees(1);
  }

  private isEmploymentStatus(value: string): value is EmploymentStatus {
    return this.employmentStatusOptions.includes(value as EmploymentStatus);
  }

  isCreateFieldMissing(field: RequiredCreateField): boolean {
    switch (field) {
      case 'employeeNumber':
        return !this.createForm.employeeNumber.trim();
      case 'firstName':
        return !this.createForm.firstName.trim();
      case 'lastName':
        return !this.createForm.lastName.trim();
      case 'hireDate':
        return !this.createForm.hireDate.trim();
      case 'phoneLocalNumber':
        return !this.createForm.phoneLocalNumber.trim();
      case 'email':
        return !this.createForm.email.trim();
      case 'departmentId':
        return this.createForm.departmentId == null;
      case 'companyId':
        return this.createForm.companyId == null;
      case 'payGroupId':
        return this.createForm.payGroupId == null;
      case 'shiftId':
        return this.createForm.shiftId == null;
      case 'workerTypeId':
        return this.createForm.workerTypeId == null;
      default:
        return false;
    }
  }

  get isCreateFormValid(): boolean {
    return (
      !this.isCreateFieldMissing('employeeNumber') &&
      !this.isCreateFieldMissing('firstName') &&
      !this.isCreateFieldMissing('lastName') &&
      !this.isCreateFieldMissing('hireDate') &&
      !this.isCreateFieldMissing('phoneLocalNumber') &&
      !this.isCreateFieldMissing('email') &&
      !this.isCreateFieldMissing('departmentId') &&
      !this.isCreateFieldMissing('companyId') &&
      !this.isCreateFieldMissing('payGroupId') &&
      !this.isCreateFieldMissing('shiftId') &&
      !this.isCreateFieldMissing('workerTypeId')
    );
  }

  private formatPhoneLocalForInput(
    localNumber: string,
    countryIso2: string | null | undefined,
  ): string {
    const digits = (localNumber ?? '').replace(/\D/g, '');

    if (!digits) return '';

    const isNorthAmerica = ['US', 'CA'].includes(String(countryIso2 ?? '').toUpperCase());

    if (isNorthAmerica) {
      const trimmed = digits.slice(0, 10);

      if (trimmed.length <= 3) {
        return trimmed;
      }

      if (trimmed.length <= 6) {
        return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`;
      }

      return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;
    }

    return digits;
  }

  private buildCreateEmployeePayload(): RegisterEmployeeBody {
    const form = this.createForm;

    const selectedCountry =
      this.countryPhoneCodes.find((country) => country.iso2 === form.phoneCountryIso2) ?? null;

    const phoneNumber = this.employeesService.buildPhoneNumberForApi(
      selectedCountry?.dialCode ?? '1',
      form.phoneLocalNumber,
    );

    const selectedDepartment =
      this.createDepartments.find((department) => department.id === form.departmentId) ?? null;

    const selectedCompany =
      this.createCompanies.find((company) => company.id === form.companyId) ?? null;

    const selectedPayGroup =
      this.createPayGroups.find((payGroup) => payGroup.id === form.payGroupId) ?? null;

    const selectedShift = this.createShifts.find((shift) => shift.id === form.shiftId) ?? null;

    const selectedWorkerType =
      this.createWorkerTypes.find((workerType) => workerType.id === form.workerTypeId) ?? null;

    const selectedSupervisor =
      this.createSupervisors.find((employee) => employee.id === form.supervisorUserId) ?? null;

    const selectedOverheadWon =
      this.createOverheadWorkOrders.find(
        (workOrder) => workOrder.id === form.defaultWorkOrderNumberId,
      ) ?? null;

    const payload: RegisterEmployeeBody = {
      employeeNumber: form.employeeNumber.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      hireDate: form.hireDate.trim(),
      phoneNumber: `+${phoneNumber ?? ''}`,
      email: form.email.trim(),
      department: Number(selectedDepartment?.code ?? 0),
      companyName: selectedCompany?.name ?? '',
      payGroup: selectedPayGroup?.name ?? '',
      shift: selectedShift?.name ?? '',
      workerType: selectedWorkerType?.name ?? '',
    };

    const middleName = form.middleName.trim();
    if (middleName) {
      payload.middleName = middleName;
    }

    const rfid = form.rfid.trim();
    if (rfid) {
      payload.rfid = rfid;
    }

    if (selectedOverheadWon?.won) {
      payload.defaultWorkOrderNumber = selectedOverheadWon.won;
    }

    const supervisorNumber = String(selectedSupervisor?.employeeNumber ?? '').trim();
    if (supervisorNumber) {
      payload.supervisorNumber = supervisorNumber;
    }

    return payload;
  }

  async createEmployee(): Promise<void> {
    if (this.isCreatingEmployee || !this.isCreateFormValid) {
      this.createSubmitError = 'Please complete all required fields.';
      this.cdr.markForCheck();
      return;
    }

    this.isCreatingEmployee = true;
    this.createSubmitError = null;
    this.cdr.markForCheck();

    try {
      const payload = this.buildCreateEmployeePayload();

      if (
        !payload.phoneNumber ||
        payload.phoneNumber === '+' ||
        !payload.companyName.trim() ||
        !payload.payGroup.trim() ||
        !payload.shift.trim() ||
        !payload.workerType.trim() ||
        !Number.isFinite(payload.department)
      ) {
        this.createSubmitError = 'Please complete all required fields.';
        return;
      }

      await firstValueFrom(this.employeesService.createEmployee(payload));

      this.ngZone.run(() => {
        this.isCreateModalOpen = false;
        this.isCreateModalLoading = false;
        this.createForm = this.getDefaultCreateForm();
        document.body.style.overflow = '';
        this.cdr.markForCheck();
      });

      await this.loadEmployees(1);

      this.showToast('success', 'Employee created successfully.');
    } catch {
      this.ngZone.run(() => {
        this.createSubmitError = 'Failed to create employee.';
        this.cdr.markForCheck();
      });

      this.showToast('error', 'Failed to create employee.');
    } finally {
      this.ngZone.run(() => {
        this.isCreatingEmployee = false;
        this.cdr.markForCheck();
      });
    }
  }

  onCompanyChange(value: number | string | null): void {
    this.filters.companyId = this.toNullableNumber(value);
    void this.loadEmployees(1);
  }

  onDepartmentChange(value: number | string | null): void {
    this.filters.departmentId = this.toNullableNumber(value);
    void this.loadEmployees(1);
  }

  onSearchTermChange(value: string | null): void {
    const normalized = String(value ?? '').trimStart();
    this.searchInput$.next(normalized);
  }

  clearFilters(): void {
    this.filters = {
      companyId: null,
      departmentId: null,
      employmentStatus: null,
      searchTerm: '',
    };

    void this.loadEmployees(1);
  }

  prevPage(): void {
    if (this.currentPage <= 1) return;
    void this.loadEmployees(this.currentPage - 1);
  }

  nextPage(): void {
    if (this.currentPage >= this.totalPages) return;
    void this.loadEmployees(this.currentPage + 1);
  }

  changePageSize(): void {
    void this.loadEmployees(1);
  }

  goToImport(): void {
    void this.router.navigate(['/employees/import']);
  }

  private readonly employeeColorCount = 8;

  getEmployeeColorClass(employee: Pick<Employee, 'id' | 'employeeNumber'>): string {
    return this.getEmployeeColorClassById(employee.id, employee.employeeNumber);
  }

  getEmployeeColorClassById(id: number | string | null | undefined, employeeNumber?: string | null): string {
    const raw = Number(id) || Number(employeeNumber) || 0;
    const index = Math.abs(raw) % this.employeeColorCount;

    return `employee-color-${index}`;
  }

  openProfile(id: number): void {
    this.router.navigate(['/employees', id], {
      queryParams: {
        employeeColor: this.getEmployeeColorClassById(id),
      },
    });
  }
}